import React, {useContext, useEffect, useState} from "react";
import {
    Box,
    Button, Checkbox,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Stack,
    Typography
} from "@mui/joy";
import {Refresh} from "@mui/icons-material";
import {GlassCard} from "../components/GlassCard";
import {Layout} from "../components/Layout";
import {APIurl, stringPrice, stripeApiKey, stripeAppearance} from "../utils/Utils";
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import {PaymentIntent, StripeElementsOptions, StripePaymentElementOptions} from "@stripe/stripe-js";
import {Price} from "../utils/Model";
import firebase from "firebase/compat/app";
import {AuthContext} from "../utils/AuthProvider";
import {FeedbackContext} from "../utils/FeedbackContext";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";

// Versione dei termini/condizioni di acquisto mostrati in questa pagina
// (checkbox di consenso) — da incrementare (es. "2026-09") ogni volta che
// il testo del consenso, i prezzi o le condizioni di rinnovo cambiano.
// Inviata al backend con ogni createPaymentIntent per la tracciabilità
// probatoria del consenso (vedi CineFX-server, subscription_consents).
const TERMS_VERSION = "2026-08";

export function PayWrapper() {

    const { t } = useTranslation();
    const {userData, subscriptions} = useContext(AuthContext)
    const {setError} = useContext(FeedbackContext)

    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);
    const [renewalPrice, setRenewalPrice] = useState<Price | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const options: StripeElementsOptions = {
        clientSecret: clientSecret ?? "",
        appearance: stripeAppearance,
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function load() {
        setLoading(true)
        setLoadError(null)
        try {
            const res = await createPaymentIntent();
            setClientSecret(res.client_secret)
            setPaymentIntent(res.payment_intent)
            setRenewalPrice(res.renewal_price ?? null)
        } catch (e: unknown) {
            setLoadError(t("paymentintenterror"))
            setError(e)
        } finally {
            setLoading(false)
        }
    }

    if (loadError) {
        return (
            <Layout>
                <Container sx={{flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2, py: 6}}>
                    <Typography level="body-md">{loadError}</Typography>
                    <Button startDecorator={<Refresh />} onClick={load}>{t("retry")}</Button>
                </Container>
            </Layout>
        )
    }

    if(clientSecret !== null && paymentIntent !== null && !loading) return (
        <Elements stripe={stripeApiKey} options={options}>
            <Pay />
        </Elements>
    )
    return (
        <Layout>
            <Box sx={{flex: 1, display:'flex', flexDirection:'column', justifyContent:'center', alignItems: 'center', py: 6}}>
                <CircularProgress />
            </Box>
        </Layout>
    )

    function Pay() {
        const stripe = useStripe();
        const elements = useElements();

        const [terms, setTerms] = useState(false);
        const [processingPayment, setProcessingPayment] = useState(false);

        // paymentIntent.amount è già il prezzo scontato del primo anno
        // (sconto applicato server-side, vedi createPaymentIntent); il
        // prezzo di rinnovo arriva ora dal server invece di essere
        // hardcoded qui — vedi routes/../index.js createPaymentIntent,
        // che ora restituisce anche `renewal_price`.
        const renewalPriceLabel = renewalPrice ? stringPrice(renewalPrice.currency, renewalPrice.unit_amount) : null;
        const discountPercent = renewalPrice && renewalPrice.unit_amount > 0
            ? Math.round((1 - paymentIntent!.amount / renewalPrice.unit_amount) * 100)
            : 0;

        const options2: StripePaymentElementOptions = {
            layout: {
                type: 'accordion',
                defaultCollapsed: false,
                radios: true,
                spacedAccordionItems: false
            }
        };

        if(subscriptions.length > 0) {
            return (
                <Layout>
                    <Container maxWidth="xs" sx={{flex: 1, display: "flex", alignItems: "center", py: {xs: 4, sm: 6}, px: {xs: 2, sm: 3}}}>
                        <GlassCard sx={{width: "100%", p: {xs: 3, sm: 4}, textAlign: "center"}}>
                            <Stack spacing={2} alignItems="center">
                                <Typography level="title-lg">{t("alreadyhavesub")}</Typography>
                                <Link to="/" style={{width: "100%"}}>
                                    <Button sx={{width: "100%"}}>
                                        {t("backtoaccount")}
                                    </Button>
                                </Link>
                            </Stack>
                        </GlassCard>
                    </Container>
                </Layout>
            )
        } else return (
            <Layout>
                <Container maxWidth="sm" sx={{py: {xs: 3, sm: 5}, px: {xs: 2, sm: 3}}}>
                    <Stack spacing={3}>
                        <Stack spacing={2}>
                            <Stack spacing={1}>
                                <Typography level="title-lg" fontWeight="bold">{t("cart")}</Typography>
                                <GlassCard>
                                    <Grid container spacing={2}>
                                        <Grid xs={6} sm={4}>
                                            <Typography level="body-sm" sx={{color: "neutral.400"}}>{t("product")}</Typography>
                                            <Typography level="title-sm" fontWeight="bold">{t("cinefx")}</Typography>
                                        </Grid>
                                        <Grid xs={6} sm={4}>
                                            <Typography level="body-sm" sx={{color: "neutral.400"}}>{t("type")}</Typography>
                                            <Typography level="title-sm" fontWeight="bold">{t("subscription")}</Typography>
                                        </Grid>
                                        <Grid xs={6} sm={4}>
                                            <Typography level="body-sm" sx={{color: "neutral.400"}}>{t("frequency")}</Typography>
                                            <Typography level="title-sm" fontWeight="bold">{t("annual")}</Typography>
                                        </Grid>
                                        {renewalPriceLabel &&
                                            <Grid xs={6} sm={4}>
                                                <Typography level="body-sm" sx={{color: "neutral.400"}}>{t("price")}</Typography>
                                                <Typography level="title-sm" fontWeight="bold" sx={{textDecoration:"line-through", color: "neutral.500"}}>{renewalPriceLabel}</Typography>
                                            </Grid>
                                        }
                                        <Grid xs={6} sm={4}>
                                            <Typography level="body-sm" sx={{color: "neutral.400"}}>{t("pricefirstyear")}</Typography>
                                            <Typography level="title-sm" fontWeight="bold">{stringPrice(paymentIntent!.currency, paymentIntent!.amount)}</Typography>
                                        </Grid>
                                        <Grid xs={12} sm={4}>
                                            <Typography level="body-sm" sx={{color: "neutral.400"}}>{t("emailcinefxaccount")}</Typography>
                                            <Typography level="title-sm" fontWeight="bold" sx={{wordBreak: "break-word"}}>{userData.email}</Typography>
                                        </Grid>
                                    </Grid>
                                </GlassCard>
                                <Stack direction="column" justifyContent="center" alignItems="center" sx={{pt:3}} spacing={1}>
                                    {discountPercent > 0 &&
                                        <Chip color="success" variant="soft">{t("discountbadge", {percent: discountPercent})}</Chip>
                                    }
                                    <Typography level="body-sm">{t("topaytoday")}</Typography>
                                    <Typography level="h2" fontWeight="bold">{stringPrice(paymentIntent!.currency, paymentIntent!.amount)}</Typography>
                                    <Typography level="body-sm" textAlign="center" sx={{color: "neutral.400"}}>
                                        {renewalPriceLabel
                                            ? <>{t("subrenewsat")} {renewalPriceLabel}. </>
                                            : null
                                        }
                                        {t("youcancancelanytime")} <a href="https://cinefx.danieledigregorio.it/terms" target="_blank" rel="noreferrer"><u>{t("termsandconditions")}</u></a>.
                                    </Typography>
                                </Stack>
                            </Stack>

                            <Stack spacing={1}>
                                <Typography level="title-md" fontWeight="bold">{t("paymentmethod")}</Typography>
                                <GlassCard sx={{p: {xs: 1.5, sm: 2}}}>
                                    <PaymentElement options={options2} />
                                </GlassCard>
                            </Stack>
                            <Checkbox label={<Typography level="body-sm">{t("declareto")} <a href="https://cinefx.danieledigregorio.it/terms" target="_blank" rel="noreferrer"><u>{t("termsandconditions")}</u></a> {t("andthe")}<a href="https://cinefx.danieledigregorio.it/privacy" target="_blank" rel="noreferrer"><u>{t("privacypolicy")}</u></a>.{renewalPriceLabel ? t("declareautorenew", {price: renewalPriceLabel}) : t("declareautorenewfallback")}</Typography>}
                                      size="sm"
                                      checked={terms}
                                      onClick={() => setTerms(!terms)}
                            />
                            <Button
                                disabled={!terms || processingPayment}
                                loading={processingPayment}
                                size="lg"
                                onClick={() => {
                                    confirmPayment()
                                }}
                            >
                                {t("pay")}
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Layout>
        )

        async function confirmPayment() {
            setProcessingPayment(true)
            try {
                await elements?.submit()
                const result = await stripe?.confirmPayment({
                    elements: elements!,
                    clientSecret: clientSecret!,
                    confirmParams: {
                        return_url: `${window.location.origin}/completion`,
                    }
                })
                if(result?.error) {
                    setError(result.error)
                }
            } catch (err) {
                setError(err)
            } finally {
                setProcessingPayment(false)
            }
        }

    }

    async function createPaymentIntent() {
        const user = firebase.auth().currentUser;
        if (!user) {
            throw new Error(t("usernotauthenticated"));
        }
        const token = await user.getIdToken();

        const response = await fetch(`${APIurl}/createPaymentIntent`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                stripeCustomerId: userData.stripeCustomerId,
                itemId: "price_1RQSovGzw2l8brmR8PtNBTvr",
                uid: user.uid,
                termsVersion: TERMS_VERSION,
                // Snapshot device al momento del consenso — sicurezza/prova
                // aggiuntiva sulla pagina di pagamento (alternativa a uno
                // screenshot, non fattibile: i campi carta vivono in iframe
                // isolati Stripe, irraggiungibili anche da noi per PCI-DSS).
                // Salvato insieme a IP/user-agent (già catturati server-side)
                // nella stessa riga subscription_consents, coperto dalla
                // stessa firma HMAC degli export.
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                browserLanguage: navigator.language,
            })
        })
        if (!response.ok) {
            throw new Error(t("genericerror"));
        }

        return await response.json();
    }
}
